import React from 'react';
import { gql } from '@apollo/client';
import { useQueryCoordinate } from '../clients/graphql/graphql.hooks';
import { Box, Grid, Paper, Typography, TextField, MenuItem, Button, Chip, IconButton, Skeleton, Divider, Autocomplete } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { BROKER_NAME } from '../shared/portfolio-types';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Clear, Save, Delete } from '@mui/icons-material';

const GET_BALANCING_STATE_METRICS_V2 = gql`
  query BalancingStateMetricsConnectionGet($input: BalancingStateMetricsConnectionGetInput, $balancingStatesInput: BalancingStatesInput) {
    balancingStateMetricsConnectionGet(input: $input) {
      stateDistribution { name count percentage }
      workRequiredDistribution { name count percentage }
      balancingStates(input: $balancingStatesInput) {
        total
        balancingStates {
          autoPilotSettingsKey
          state
          workRequiredAttributionState
          workflowId
          createdAt
          stateType
          workRequiredAttributionStateType
        }
      }
    }
  }
`;

// Marketplace search for teams and portfolios
const MARKETPLACE_SEARCH = gql`
  query MarketPlaceSearch($input: MarketPlaceSearchInput!) {
    marketPlaceSearch(input: $input) {
      teams { teamKey title }
      portfolios {
        portfolioKey
        autoPilotMasterPortfolio { title subtitle description team { title } }
        user { firstName lastName username }
      }
    }
  }
`;

const STATE_NAME_TO_CODE: Record<string, number> = {
  'Terminal': 0,
  'In Progress': 1,
  'Failed': 2,
};

const WORK_NAME_TO_CODE: Record<string, number> = {
  'No work required': 0,
  'Platform Issue': 1,
  'User Issue': 2,
  'Broker Issue': 3,
};

const STATE_COLORS: Record<string, string> = {
  'Terminal': '#10b981',
  'In Progress': '#3b82f6',
  'Failed': '#ef4444',
};

const WORK_COLORS: Record<string, string> = {
  'No work required': '#10b981',
  'Platform Issue': '#f59e0b',
  'User Issue': '#3b82f6',
  'Broker Issue': '#ef4444',
};

// Table label helpers
const STATE_CODE_TO_NAME: Record<number, string> = {
  0: 'TERMINAL',
  1: 'IN_PROGRESS',
  2: 'FAILED',
};

const WRAS_CODE_TO_NAME: Record<number, string> = {
  0: 'NO_WORK_REQUIRED',
  1: 'PLATFORM',
  2: 'USER',
  3: 'BROKER',
};

export const BalancingStatesMetricsV2: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [teamKey, setTeamKey] = React.useState<string>('');
  const [selectedTeamTitle, setSelectedTeamTitle] = React.useState<string>('');
  const [portfolioKey, setPortfolioKey] = React.useState<string>('');
  const [brokerName, setBrokerName] = React.useState<BROKER_NAME | ''>('');
  const [selectedState, setSelectedState] = React.useState<number | undefined>(undefined);
  const [selectedWork, setSelectedWork] = React.useState<number | undefined>(undefined);
  const [limit, setLimit] = React.useState<number>(25);
  const [offset, setOffset] = React.useState<number>(0);

  // Load from URL once
  React.useEffect(() => {
    const tk = searchParams.get('teamKey');
    const pk = searchParams.get('portfolioKey');
    const bn = searchParams.get('brokerName') as BROKER_NAME | null;
    const st = searchParams.get('state');
    const wr = searchParams.get('work');
    const lm = searchParams.get('limit');
    const os = searchParams.get('offset');
    if (tk) setTeamKey(tk);
    if (pk) setPortfolioKey(pk);
    if (bn) setBrokerName(bn);
    if (st !== null) setSelectedState(st === '' ? undefined : Number(st));
    if (wr !== null) setSelectedWork(wr === '' ? undefined : Number(wr));
    if (lm) setLimit(Number(lm));
    if (os) setOffset(Number(os));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Team search state
  const [teamSearchValue, setTeamSearchValue] = React.useState<string>('');
  const [teamOpen, setTeamOpen] = React.useState<boolean>(false);
  const { data: teamSearchData } = useQueryCoordinate(MARKETPLACE_SEARCH, {
    variables: { input: { searchValue: teamSearchValue || '', limitTeams: 8, offsetTeams: 0, limitPortfolios: 0, offsetPortfolios: 0 } },
    skip: !teamOpen,
  });

  // Portfolio search state (filtered by selected team title when present)
  const [portfolioSearchValue, setPortfolioSearchValue] = React.useState<string>('');
  const [portfolioOpen, setPortfolioOpen] = React.useState<boolean>(false);
  const { data: portfolioSearchData } = useQueryCoordinate(MARKETPLACE_SEARCH, {
    variables: { input: { searchValue: portfolioSearchValue || '', limitTeams: 0, offsetTeams: 0, limitPortfolios: 10, offsetPortfolios: 0 } },
    skip: !portfolioOpen,
  });

  const variables = React.useMemo(() => {
    const input: any = {};
    if (teamKey) input.teamKey = Number(teamKey);
    if (portfolioKey) input.portfolioKey = Number(portfolioKey);
    if (brokerName) input.brokerName = brokerName;
    if (typeof selectedState === 'number') input.state = selectedState;
    if (typeof selectedWork === 'number') input.workRequiredAttributionState = selectedWork;

    const balancingStatesInput: any = {
      limit,
      offset,
    };
    if (typeof selectedState === 'number') balancingStatesInput.state = selectedState;
    if (typeof selectedWork === 'number') balancingStatesInput.workRequiredAttributionState = selectedWork;

    return { input, balancingStatesInput };
  }, [teamKey, portfolioKey, brokerName, selectedState, selectedWork, limit, offset]);

  const { data, loading, error, refetch } = useQueryCoordinate(GET_BALANCING_STATE_METRICS_V2, {
    variables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  // URL sync
  React.useEffect(() => {
    const params: Record<string, string> = {};
    if (teamKey) params.teamKey = teamKey;
    if (portfolioKey) params.portfolioKey = portfolioKey;
    if (brokerName) params.brokerName = String(brokerName);
    if (typeof selectedState === 'number') params.state = String(selectedState);
    if (typeof selectedWork === 'number') params.work = String(selectedWork);
    params.limit = String(limit);
    params.offset = String(offset);
    setSearchParams(params, { replace: true });
  }, [teamKey, portfolioKey, brokerName, selectedState, selectedWork, limit, offset, setSearchParams]);

  // Presets
  type Preset = { name: string, input: { teamKey?: string, portfolioKey?: string, brokerName?: string, state?: number, work?: number } };
  const PRESETS_KEY = 'balancingStatesV2.presets';
  const [presets, setPresets] = React.useState<Preset[]>(() => {
    try { return JSON.parse(localStorage.getItem(PRESETS_KEY) || '[]') || []; } catch { return []; }
  });
  const [newPresetName, setNewPresetName] = React.useState<string>('');
  const savePreset = () => {
    if (!newPresetName.trim()) return;
    const next: Preset[] = [
      ...presets.filter(p => p.name !== newPresetName.trim()),
      {
        name: newPresetName.trim(),
        input: {
          teamKey: teamKey || undefined,
          portfolioKey: portfolioKey || undefined,
          brokerName: (brokerName as string) || undefined,
          state: selectedState,
          work: selectedWork,
        }
      }
    ];
    setPresets(next);
    localStorage.setItem(PRESETS_KEY, JSON.stringify(next));
    setNewPresetName('');
  };
  const applyPreset = (preset: Preset) => {
    setTeamKey(preset.input.teamKey || '');
    setPortfolioKey(preset.input.portfolioKey || '');
    setBrokerName((preset.input.brokerName as BROKER_NAME) || '');
    setSelectedState(preset.input.state);
    setSelectedWork(preset.input.work);
    setOffset(0);
  };
  const deletePreset = (name: string) => {
    const next = presets.filter(p => p.name !== name);
    setPresets(next);
    localStorage.setItem(PRESETS_KEY, JSON.stringify(next));
  };

  const stateDistribution = data?.balancingStateMetricsConnectionGet?.stateDistribution || [];
  const workRequiredDistribution = data?.balancingStateMetricsConnectionGet?.workRequiredDistribution || [];
  const response = data?.balancingStateMetricsConnectionGet?.balancingStates;
  const items = response?.balancingStates || [];
  const total = response?.total || 0;

  const onSelectStateSlice = (entry: any) => {
    const code = STATE_NAME_TO_CODE[entry.name];
    if (typeof code === 'number') {
      // Navigate to drilldown page with current filters + state
      const params: Record<string, string> = {};
      if (teamKey) params.teamKey = teamKey;
      if (portfolioKey) params.portfolioKey = portfolioKey;
      if (brokerName) params.brokerName = String(brokerName);
      params.state = String(code);
      const qs = new URLSearchParams(params).toString();
      navigate(`/balancing-states-metrics/drilldown?${qs}`);
    }
  };

  const onSelectWorkSlice = (entry: any) => {
    const code = WORK_NAME_TO_CODE[entry.name];
    if (typeof code === 'number') {
      setSelectedWork(prev => (prev === code ? undefined : code));
      setOffset(0);
    }
  };

  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>Balancing State Metrics V2</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              freeSolo
              open={teamOpen}
              onOpen={() => { setTeamOpen(true); setTeamSearchValue(''); }}
              onClose={() => setTeamOpen(false)}
              options={(teamSearchData?.marketPlaceSearch?.teams || []).map((t: any) => ({ label: `${t.title} (#${t.teamKey})`, teamKey: String(t.teamKey), title: t.title }))}
              inputValue={teamSearchValue}
              onInputChange={(_, v) => setTeamSearchValue(v)}
              onChange={(_, val: any) => {
                if (val && typeof val === 'object') {
                  setTeamKey(val.teamKey);
                  setSelectedTeamTitle(val.title || '');
                } else if (typeof val === 'string') {
                  setTeamKey(val.trim());
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Pilot Team" size="small" placeholder="Search by name or enter key" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              freeSolo
              open={portfolioOpen}
              onOpen={() => { setPortfolioOpen(true); setPortfolioSearchValue(''); }}
              onClose={() => setPortfolioOpen(false)}
              options={(() => {
                const all = (portfolioSearchData?.marketPlaceSearch?.portfolios || []).map((p: any) => ({
                  label: `${p.autoPilotMasterPortfolio?.title || 'Portfolio'} (#${p.portfolioKey})`,
                  portfolioKey: String(p.portfolioKey),
                  teamTitle: p.autoPilotMasterPortfolio?.team?.title || '',
                }));
                if (selectedTeamTitle) return all.filter((o: any) => o.teamTitle === selectedTeamTitle);
                return all;
              })()}
              inputValue={portfolioSearchValue}
              onInputChange={(_, v) => setPortfolioSearchValue(v)}
              onChange={(_, val: any) => {
                if (val && typeof val === 'object') {
                  setPortfolioKey(val.portfolioKey);
                } else if (typeof val === 'string') {
                  setPortfolioKey(val.trim());
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Master Portfolio" size="small" placeholder="Search by name or enter key" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Broker"
              value={brokerName}
              onChange={(e) => setBrokerName(e.target.value as BROKER_NAME)}
              fullWidth
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              {Object.keys(BROKER_NAME).map((k) => (
                <MenuItem key={k} value={(BROKER_NAME as any)[k]}>{k}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={() => { setOffset(0); refetch(); }}>Apply</Button>
              <Button variant="text" startIcon={<Clear />} onClick={() => { setTeamKey(''); setPortfolioKey(''); setBrokerName(''); setSelectedState(undefined); setSelectedWork(undefined); setOffset(0); refetch(); }}>Clear</Button>
              {typeof selectedState === 'number' && (
                <Chip label={`State: ${Object.keys(STATE_NAME_TO_CODE).find(n => STATE_NAME_TO_CODE[n] === selectedState)}`} onDelete={() => setSelectedState(undefined)} />
              )}
              {typeof selectedWork === 'number' && (
                <Chip label={`Work: ${Object.keys(WORK_NAME_TO_CODE).find(n => WORK_NAME_TO_CODE[n] === selectedWork)}`} onDelete={() => setSelectedWork(undefined)} />
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField size="small" label="Preset name" value={newPresetName} onChange={(e) => setNewPresetName(e.target.value)} sx={{ width: 240 }} />
              <Button variant="outlined" startIcon={<Save />} onClick={savePreset}>Save preset</Button>
              {presets.length > 0 && (
                <>
                  <Typography variant="body2" sx={{ ml: 1, mr: 1, color: 'text.secondary' }}>Presets:</Typography>
                  {presets.map((p) => (
                    <Box key={p.name} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, px: 1, py: 0.5 }}>
                      <Typography variant="body2" sx={{ mr: 0.5 }}>{p.name}</Typography>
                      <Button size="small" onClick={() => applyPreset(p)}>Apply</Button>
                      <IconButton size="small" onClick={() => deletePreset(p.name)}><Delete fontSize="small" /></IconButton>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>State Distribution</Typography>
            <Box sx={{ height: 320 }}>
              {loading && stateDistribution.length === 0 ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={stateDistribution} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={100} onClick={onSelectStateSlice}>
                      {stateDistribution.map((entry: any, idx: number) => (
                        <Cell key={idx} fill={STATE_COLORS[entry.name] || '#64748b'} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Counts</Typography>
            <Box>
              {loading && stateDistribution.length === 0 ? (
                <Skeleton variant="rectangular" height={300} />
              ) : (
                <Box>
                  {stateDistribution.map((d: any, idx: number) => (
                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: STATE_COLORS[d.name] || '#64748b' }} />
                        <Typography variant="body2">{d.name}</Typography>
                      </Box>
                      <Typography variant="body2">{d.count} ({d.percentage}%)</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      {/* The drilldown table lives on the dedicated drilldown page */}
    </Box>
  );
};


